/**
 * What signup accepts, stated where the player can see it before they
 * press the button. The server decides (`routes/accounts.rs`:
 * `validate_signup_username` / `validate_signup_password`); these are
 * the same rules, so the form can say which one is unmet instead of the
 * player learning them one refusal at a time.
 *
 * No composition rules ("a digit and a symbol") — NIST SP 800-63B
 * advises against them. Length, not the username, not a common password.
 */

export const USERNAME_MIN_CHARS = 3;
export const USERNAME_MAX_CHARS = 32;
export const PASSWORD_MIN_CHARS = 8;
export const PASSWORD_MAX_CHARS = 128;

const USERNAME_CHARSET = /^[A-Za-z0-9_]*$/;

/** Characters as the server counts them (Unicode scalar values), so a
 *  password of emoji or Hangul measures the same on both sides. */
function charCount(s: string): number {
  return [...s].length;
}

export interface UsernameChecks {
  readonly length: boolean;
  readonly charset: boolean;
}

export function checkUsername(username: string): UsernameChecks {
  const n = charCount(username);
  return {
    length: n >= USERNAME_MIN_CHARS && n <= USERNAME_MAX_CHARS,
    charset: USERNAME_CHARSET.test(username),
  };
}

export interface PasswordChecks {
  readonly minLength: boolean;
  readonly maxLength: boolean;
  readonly notUsername: boolean;
  /** `null` while the list is still loading — unknown, not passed. */
  readonly notCommon: boolean | null;
}

export function checkPassword(
  username: string,
  password: string,
  common: ReadonlySet<string> | null,
): PasswordChecks {
  const n = charCount(password);
  const lowered = password.toLowerCase();
  return {
    minLength: n >= PASSWORD_MIN_CHARS,
    maxLength: n <= PASSWORD_MAX_CHARS,
    notUsername: lowered !== username.trim().toLowerCase(),
    notCommon: common === null ? null : !common.has(lowered),
  };
}

/** The deny-list the server compiles in, loaded only when the signup
 *  tab opens so the login screen does not pay for it. */
let commonPasswords: Promise<ReadonlySet<string>> | null = null;

export function loadCommonPasswords(): Promise<ReadonlySet<string>> {
  commonPasswords ??= import("../../../assets/passwords/common.txt?raw").then(
    (mod: { default: string }) =>
      new Set(
        mod.default
          .split("\n")
          .map((l) => l.trim())
          .filter((l) => l !== "" && !l.startsWith("#")),
      ),
  );
  // A failed load is retried on the next call; meanwhile the form
  // treats the check as unknown and leaves it to the server.
  commonPasswords.catch(() => {
    commonPasswords = null;
  });
  return commonPasswords;
}
